import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { addDaysISO, todayISO } from '../utils/dates';
import { saveBlobResponse } from '../utils/download';

import DateInput from './DateInput';
const PRESETS = [
  { key: 'today', label: 'Today', days: 0 },
  { key: 'week', label: 'Last 7 days', days: 6 },
  { key: 'month', label: 'Last 30 days', days: 29 },
  { key: 'custom', label: 'Custom dates' }
];

const ACTIONS = {
  CREATE: { label: 'Created', bg: 'success' },
  UPDATE: { label: 'Changed', bg: 'primary' },
  DELETE: { label: 'Deleted', bg: 'danger' }
};

// Field names as people say them; the rest are spelt out from the code's names ("unitPrice" -> "Unit price")
const FIELD_NAMES = {
  cgstAmount: 'CGST', sgstAmount: 'SGST', igstAmount: 'IGST', taxId: 'GSTIN', hsnCode: 'HSN', hsCode: 'HSN',
  gstin: 'GSTIN', pan: 'PAN', tan: 'TAN', upiId: 'UPI ID', ifscCode: 'IFSC', poNumber: 'PO number', poDate: 'PO date',
  grnNumber: 'GRN number', ginNumber: 'GIN number', rop: 'Reorder point', ssq: 'Safety stock', moq: 'Minimum order',
  poq: 'Preferred order', msmeCategory: 'MSME category', tdsSection: 'TDS section', interState: 'Inter-state',
  isInterState: 'Inter-state', isActive: 'Active', grnLineItem: 'Lot', taxRatePercent: 'GST rate %'
};

export const fieldName = (f) => FIELD_NAMES[f] || (f || '').replace(/LedgerId$/, 'Ledger').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()
  .replace(/^./, (c) => c.toUpperCase()).replace(/\bid\b/i, 'ID');

const when = (s) => new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** Rows of the edit log: when, who, what, and each field's old and new value (created and deleted records fold away). */
export function EditLogTable({ entries, emptyText, showWhat = true }) {
  const [open, setOpen] = useState({});
  return (
    <Table size="sm" bordered hover responsive>
      <thead>
        <tr><th style={{ width: '9rem' }}>When</th><th>Who</th><th>What</th><th>Changes</th></tr>
      </thead>
      <tbody>
        {entries.map((e) => {
          const action = ACTIONS[e.action] || ACTIONS.UPDATE;
          const snapshot = e.action !== 'UPDATE';
          const shown = snapshot && !open[e.id] ? [] : e.changes;
          return (
            <tr key={e.id}>
              <td className="text-nowrap small">{when(e.at)}</td>
              <td className="small">{e.user === 'system' ? <span className="text-muted">The system</span> : e.user}</td>
              <td className="small">
                <Badge bg={action.bg} className="me-1">{action.label}</Badge>
                {showWhat
                  ? <>{e.line ? 'a line of ' : ''}{e.documentLabel} <strong>{e.reference || `#${e.documentId}`}</strong></>
                  : (e.line ? 'a line' : '')}
              </td>
              <td className="small">
                {shown.map((c) => (
                  <div key={c.field}>
                    <span className="text-muted">{fieldName(c.field)}:</span>{' '}
                    {e.action === 'UPDATE' ? (
                      <><span className="text-decoration-line-through text-muted">{c.from || '(blank)'}</span> &rarr; {c.to || '(blank)'}</>
                    ) : (e.action === 'DELETE' ? c.from : c.to)}
                  </div>
                ))}
                {snapshot && e.changes.length > 0 && (
                  <button type="button" className="btn btn-link btn-sm p-0 text-start" onClick={() => setOpen((o) => ({ ...o, [e.id]: !o[e.id] }))}>
                    {open[e.id] ? 'Hide' : `Show what it ${e.action === 'DELETE' ? 'held' : 'was created with'} (${e.changes.length})`}
                  </button>
                )}
              </td>
            </tr>
          );
        })}
        {entries.length === 0 && (
          <tr><td colSpan={4} className="text-center text-muted">{emptyText}</td></tr>
        )}
      </tbody>
    </Table>
  );
}

/**
 * Org Admin > Edit Log: who created, changed or deleted which document or master record, and when, with each field's
 * old and new value. Read and download only: nobody can change or remove the log.
 */
function EditLog({ active }) {
  const [preset, setPreset] = useState('week');
  const [period, setPeriod] = useState({ from: addDaysISO(todayISO(), -6), to: todayISO() });
  const [documentType, setDocumentType] = useState('');
  const [user, setUser] = useState('');
  const [reference, setReference] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState('');

  const params = useCallback(() => {
    const p = new URLSearchParams({ from: period.from, to: period.to });
    if (documentType) p.set('documentType', documentType);
    if (user) p.set('user', user);
    if (search) p.set('reference', search);
    return p;
  }, [period, documentType, user, search]);

  const load = useCallback(async () => {
    if (!period.from || !period.to) return;
    try {
      setLoading(true);
      setProblem('');
      const p = params();
      p.set('page', page);
      const r = await axios.get(`/api/edit-log?${p}`);
      setData(r.data);
    } catch (error) {
      const msg = typeof error.response?.data === 'string' && error.response.data ? error.response.data : 'Could not load the edit log';
      setProblem(msg);
    } finally {
      setLoading(false);
    }
  }, [params, page, period]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const choosePreset = (key) => {
    setPreset(key);
    const p = PRESETS.find((x) => x.key === key);
    if (p && p.days != null) setPeriod({ from: addDaysISO(todayISO(), -p.days), to: todayISO() });
    setPage(0);
  };

  const download = async () => {
    try {
      const r = await axios.get(`/api/edit-log/export?${params()}`, { responseType: 'blob' });
      saveBlobResponse(r, `edit-log-${period.from}-to-${period.to}.csv`);
    } catch (error) {
      toast.error('Could not download the edit log');
    }
  };

  const types = data?.documentTypes || {};

  return (
    <div>
      <p className="text-muted small">
        Every bill, note, receipt, payment, order, stock document and journal, their lines, and your customers, vendors,
        products, materials, ledgers and bank accounts: who created, changed or deleted them, when, and what each field was
        before and after. It is always on, and nobody can change or remove it.
      </p>
      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col xs={12} sm="auto">
              <Form.Label className="small mb-1">Period</Form.Label>
              <Form.Select size="sm" value={preset} onChange={(e) => choosePreset(e.target.value)}>
                {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </Form.Select>
            </Col>
            <Col xs={6} sm="auto">
              <Form.Label className="small mb-1">From</Form.Label>
              <DateInput size="sm" value={period.from}
                onChange={(e) => { setPreset('custom'); setPage(0); setPeriod((p) => ({ ...p, from: e.target.value })); }} />
            </Col>
            <Col xs={6} sm="auto">
              <Form.Label className="small mb-1">To</Form.Label>
              <DateInput size="sm" value={period.to}
                onChange={(e) => { setPreset('custom'); setPage(0); setPeriod((p) => ({ ...p, to: e.target.value })); }} />
            </Col>
            <Col xs={12} sm="auto">
              <Form.Label className="small mb-1">What</Form.Label>
              <Form.Select size="sm" value={documentType} onChange={(e) => { setDocumentType(e.target.value); setPage(0); }}>
                <option value="">Everything</option>
                {Object.entries(types).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </Form.Select>
            </Col>
            <Col xs={12} sm="auto">
              <Form.Label className="small mb-1">Who</Form.Label>
              <Form.Select size="sm" value={user} onChange={(e) => { setUser(e.target.value); setPage(0); }}>
                <option value="">Everyone</option>
                {(data?.users || []).map((u) => <option key={u} value={u}>{u === 'system' ? 'The system (scheduled jobs)' : u}</option>)}
              </Form.Select>
            </Col>
            <Col xs={12} sm="auto">
              <Form.Label className="small mb-1">Number or name</Form.Label>
              <Form.Control size="sm" placeholder="SB-2026-10-0001" value={reference} onChange={(e) => setReference(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(reference.trim()); setPage(0); } }}
                onBlur={() => { if (reference.trim() !== search) { setSearch(reference.trim()); setPage(0); } }} />
            </Col>
            <Col xs={12} sm className="d-flex justify-content-sm-end">
              <Button size="sm" variant="outline-secondary" onClick={download} disabled={!data || data.total === 0}>Download CSV</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {problem && <Alert variant="danger">{problem}</Alert>}
      {loading && !data && <div className="text-center py-4"><Spinner animation="border" /></div>}

      {data && (
        <>
          <EditLogTable entries={data.entries} emptyText="Nothing was created, changed or deleted in this period." />
          {data.pages > 1 && (
            <div className="d-flex align-items-center gap-2">
              <Button size="sm" variant="outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Newer</Button>
              <span className="small text-muted">Page {page + 1} of {data.pages} ({data.total} entries)</span>
              <Button size="sm" variant="outline-secondary" disabled={page + 1 >= data.pages} onClick={() => setPage(page + 1)}>Older</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EditLog;
